export const heroData = {
  headline: "The Partner Banks Trust for Secure Voice Systems and Elite Technology Talent",
  subheadline:
    "When regulatory compliance and system uptime can't be compromised, leading financial institutions turn to RedeemSoft. For over two decades, we've delivered enterprise-grade call recording infrastructure, compliance solutions, and the specialized IT professionals who keep mission-critical systems running.",
  ctaPrimary: "Speak With an Enterprise Specialist",
  ctaSecondary: "Explore Our Solutions",
  badge: "Serving Banking & Enterprise Since 2004",
};

export const trustBarData = {
  label: "Trusted Technology Partner To",
  clients: [
    { name: "Fortune 100 Financial Services", logo: "ri-bank-line" },
    { name: "Top 10 Global Investment Bank", logo: "ri-briefcase-line" },
    { name: "National Insurance Carrier", logo: "ri-shield-check-line" },
    { name: "Regional Healthcare System", logo: "ri-heart-pulse-line" },
    { name: "Federal Financial Regulator", logo: "ri-government-line" },
    { name: "Enterprise SaaS Platform", logo: "ri-exchange-line" },
  ],
};

export const aboutData = {
  label: "Who We Are",
  headline: "We don't just supply technology or people. We solve problems that keep executives up at night.",
  paragraphs: [
    "In 2004, a small team of engineers and recruiters in Iselin, New Jersey set out to do something unconventional: build a company that could deliver both world-class banking technology and the talent to run it — under one roof. The idea was simple. Banks and financial institutions were tired of juggling multiple vendors who didn't understand their regulatory reality. They needed a partner who spoke their language.",
    "Today, that team has grown into RedeemSoft — a trusted partner to over 50 enterprise clients across banking, financial services, insurance, and healthcare. We've deployed compliance recording systems for trading floors handling billions in daily volume. We've placed CTOs who went on to lead digital transformations. And we've done it all with a 98% client retention rate that we're quietly proud of.",
    "We're still headquartered in New Jersey. We're still founder-led. And we still answer the phone on the second ring.",
  ],
  stats: [
    { value: "20+", label: "Years Serving Enterprise", detail: "Consistent delivery since 2004" },
    { value: "50+", label: "Enterprise Clients", detail: "Across banking, insurance, healthcare" },
    { value: "500+", label: "Successful Placements", detail: "From contract roles to C-suite hires" },
    { value: "98%", label: "Client Retention Rate", detail: "Measured annually over the last decade" },
  ],
};

export const servicesData = {
  label: "Our Services",
  headline: "Two deeply connected capabilities. One accountable partner.",
  subheadline:
    "Every bank needs secure technology and the people to run it. We deliver both, which means you spend less time managing vendors and more time moving your business forward.",
  tabs: [
    {
      id: "technology",
      label: "Banking Technology",
      title: "Enterprise Technology for Financial Institutions",
      description:
        "We architect, deploy, and support the systems that banks depend on — from call recording infrastructure that satisfies SEC and FINRA auditors to secure applications that handle millions of transactions daily.",
      offerings: [
        {
          icon: "ri-mic-2-line",
          title: "Call & Voice Recording",
          description:
            "A single platform for turret, VoIP, and mobile recording with unified search, playback, and evidence management. Designed for trading floors, advisory desks, and 24/7 contact centers.",
          highlights: ["Turret & VoIP capture", "Immutable storage", "Role-based access", "Unified search & replay"],
        },
        {
          icon: "ri-file-shield-line",
          title: "Compliance Recording",
          description:
            "Regulatory-grade recording systems that satisfy MiFID II, Dodd-Frank, SEC, FINRA, and GDPR. Every interaction is captured, timestamped, and stored in tamper-evident format — ready for audit at any moment.",
          highlights: ["MiFID II / SEC / FINRA ready", "Tamper-evident audit trails", "Automated retention policies", "Supervisor oversight dashboards"],
        },
        {
          icon: "ri-server-line",
          title: "Enterprise Software & Integration",
          description:
            "Full-lifecycle implementation of core banking platforms, CRM systems, and custom enterprise applications. We handle architecture, deployment, integration, testing, and ongoing support.",
          highlights: ["Core banking platforms", "CRM & workflow automation", "API & system integration", "Managed support & maintenance"],
        },
        {
          icon: "ri-refresh-line",
          title: "Digital Transformation",
          description:
            "Legacy modernization, cloud migration, and process automation for financial services organizations. We move you from brittle, on-premise systems to resilient, scalable infrastructure — without disrupting operations.",
          highlights: ["Cloud migration (AWS/Azure/GCP)", "Legacy system modernization", "Workflow automation", "Zero-downtime transitions"],
        },
      ],
    },
    {
      id: "recruitment",
      label: "IT Staffing & Recruitment",
      title: "Quality Talent, Delivered With Precision",
      description:
        "We don't spray resumes. We understand your technology stack, your team culture, and the specific outcomes you need. Then we find professionals who fit — usually within two weeks for contract roles.",
      offerings: [
        {
          icon: "ri-user-search-line",
          title: "Contract & Project Staffing",
          description:
            "Pre-vetted technology professionals ready to contribute from day one. Average time-to-fill: 14 days. Every candidate goes through technical screening, behavioral assessment, and background verification.",
          highlights: ["14-day avg time-to-fill", "Technical skills verification", "Cultural fit assessment", "Background checked"],
        },
        {
          icon: "ri-briefcase-line",
          title: "Permanent Placement",
          description:
            "Strategic search for full-time technology roles from senior engineer to CTO. We maintain a curated network of passive candidates who aren't on job boards — the people you actually want to hire.",
          highlights: ["Passive candidate network", "3-week shortlist delivery", "Executive search capability", "90-day placement guarantee"],
        },
        {
          icon: "ri-team-line",
          title: "Recruitment Process Outsourcing",
          description:
            "End-to-end RPO that reduces cost-per-hire while improving candidate quality. Our embedded teams operate as your talent function — handling sourcing, screening, coordination, and offer management.",
          highlights: ["30% avg cost reduction", "Dedicated recruitment team", "Full-cycle management", "Scalable up or down"],
        },
        {
          icon: "ri-vip-crown-line",
          title: "Executive & Leadership Hiring",
          description:
            "Discreet, high-touch search for CIOs, CTOs, VPs of Engineering, and technology directors. We understand the stakes of leadership hires and run a rigorous process that protects your confidentiality.",
          highlights: ["Confidential search process", "Leadership competency assessment", "Board-level presentation ready", "4-8 week typical timeline"],
        },
        {
          icon: "ri-graduation-cap-line",
          title: "Training & Placement",
          description:
            "Structured technology training programs paired with guaranteed placement support. We upskill professionals in high-demand technologies and connect them directly with enterprise hiring managers who need verified, job-ready talent.",
          highlights: ["Technology bootcamp programs", "Enterprise certification prep", "Direct hiring manager connections", "Post-placement mentoring"],
        },
        {
          icon: "ri-briefcase-4-line",
          title: "Projects & Outsourcing",
          description:
            "End-to-end project delivery with dedicated offshore and nearshore teams. We manage the full project lifecycle — from requirements and architecture through development, testing, and deployment — with transparent reporting at every milestone.",
          highlights: ["Dedicated project teams", "Offshore & nearshore delivery", "Full lifecycle management", "Transparent milestone reporting"],
        },
      ],
    },
  ],
};

export const industriesData = {
  label: "Industries",
  headline: "Deep expertise in the sectors that can't afford mistakes.",
  subheadline:
    "We've spent two decades inside banks, trading floors, insurance carriers, health systems, and government agencies. We understand their regulatory environments, their technology stacks, and their pain points.",
  industries: [
    {
      icon: "ri-bank-line",
      name: "Banking & Capital Markets",
      description:
        "Retail banks, investment banks, and broker-dealers trust us with compliance recording, trading floor technology, and specialized IT talent. We know what a REG SCI audit looks like.",
      stats: "30+ banking clients",
    },
    {
      icon: "ri-line-chart-line",
      name: "Financial Services",
      description:
        "Wealth managers, fintech firms, and payment processors rely on our secure systems and compliance expertise. Our teams understand the difference between a nice-to-have and a regulatory requirement.",
      stats: "200+ technology projects",
    },
    {
      icon: "ri-shield-star-line",
      name: "Insurance",
      description:
        "Carriers and agencies trust us with infrastructure modernization and specialized staffing. We understand NAIC model laws and the unique technology challenges of underwriting and claims systems.",
      stats: "15+ year insurance partnerships",
    },
    {
      icon: "ri-heart-pulse-line",
      name: "Healthcare",
      description:
        "Health systems and payers with HIPAA-compliant technology requirements. We bring experience with EHR systems, interoperability standards, and the security frameworks healthcare demands.",
      stats: "HIPAA-compliant delivery",
    },
    {
      icon: "ri-computer-line",
      name: "Enterprise Technology",
      description:
        "SaaS companies, infrastructure providers, and product organizations scaling their engineering and operations teams. We understand modern development practices and cloud-native architectures.",
      stats: "500+ tech placements",
    },
    {
      icon: "ri-government-line",
      name: "Government & Public Sector",
      description:
        "Federal and state agencies requiring cleared technology professionals and secure solutions. We maintain relationships with candidates holding active security clearances.",
      stats: "Cleared professional network",
    },
  ],
};

export const whyChooseData = {
  label: "Why RedeemSoft",
  headline: "What makes enterprises stay with us for over a decade.",
  subheadline:
    "It's not complicated. We deliver what we promise, we don't over-engineer our proposals, and we treat every engagement like it determines our reputation — because it does.",
  reasons: [
    {
      icon: "ri-building-2-line",
      title: "One Vendor, Two Capabilities",
      description:
        "Most companies make you choose between a technology partner and a staffing firm. We give you both, which means fewer vendors, deeper institutional knowledge, and faster problem resolution.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Regulatory Literacy",
      description:
        "We don't just read about MiFID II and Dodd-Frank. We build systems that satisfy their specific requirements. Our compliance engineers understand the regulatory text — not just the marketing summary.",
    },
    {
      icon: "ri-timer-line",
      title: "Speed That Doesn't Sacrifice Quality",
      description:
        "Average 14-day time-to-fill for contract IT roles. Technology projects delivered on committed timelines. We move fast because we've done this thousands of times before.",
    },
    {
      icon: "ri-group-line",
      title: "Founder-Led, Client-Obsessed",
      description:
        "We're not a division of a conglomerate. Our founders are still involved in client relationships. When you call with an urgent need, you reach someone who can actually make decisions.",
    },
    {
      icon: "ri-global-line",
      title: "United States, Europe & Asia Delivery",
      description:
        "Headquartered in New Jersey with delivery capabilities across North America, Europe, and Asia through our vetted partner network. Follow-the-sun support when you need it.",
    },
    {
      icon: "ri-award-line",
      title: "Verifiable Track Record",
      description:
        "98% client retention over a decade. 50+ enterprise organizations. 500+ successful placements. We're happy to provide references from clients who've been with us for 5, 10, and 15+ years.",
    },
  ],
};

export const processData = {
  label: "How We Work",
  headline: "A process refined over 5,000+ engagements.",
  subheadline:
    "Whether deploying a compliance recording system across 2,000 trading desks or filling a critical CTO vacancy, our approach is structured, transparent, and designed to minimize your time investment.",
  steps: [
    {
      number: "01",
      title: "Discovery & Audit",
      duration: "Week 1",
      description:
        "We listen first. Our team conducts stakeholder interviews, reviews your current architecture or team structure, and identifies gaps, risks, and opportunities. You receive a concise findings document, not a 40-page consultant deck.",
      deliverable: "Current-state assessment & opportunity map",
    },
    {
      number: "02",
      title: "Solution Design",
      duration: "Weeks 2-3",
      description:
        "For technology projects: architecture blueprint, vendor selection, and implementation roadmap. For staffing: we define role requirements, build a sourcing strategy, and begin our curated candidate pipeline. You see shortlisted profiles by end of week 3.",
      deliverable: "Architecture blueprint or shortlisted candidate profiles",
    },
    {
      number: "03",
      title: "Deployment & Placement",
      duration: "Weeks 4+",
      description:
        "Technology: phased rollout with testing gates at each milestone. Staffing: interview coordination, offer negotiation support, and onboarding assistance. We handle the logistics so your team stays focused.",
      deliverable: "Production deployment or signed offer acceptance",
    },
    {
      number: "04",
      title: "Ongoing Partnership",
      duration: "Continuous",
      description:
        "Post-delivery, we don't disappear. We provide monitoring, support, and periodic business reviews. For placements, we check in at 30, 60, and 90 days. Our goal is to earn the next engagement — not just close the current one.",
      deliverable: "Quarterly business reviews & satisfaction audits",
    },
  ],
};

export const caseStudiesData = {
  label: "Client Outcomes",
  headline: "Real results from real engagements.",
  subheadline:
    "We don't publish sanitized case studies that read like marketing brochures. Here's what actually happened when our clients trusted us with their most critical systems and hires.",
  cases: [
    {
      industry: "Investment Banking",
      title: "Compliance Recording Migration for 2,400 Trading Desks",
      challenge: "A top-10 investment bank needed to replace their legacy recording infrastructure across three continents — with zero trading floor downtime and full regulatory audit trail preservation.",
      solution: "We designed and deployed a next-generation recording architecture with phased cutover, parallel running during transition, and automated data validation across 2,400 positions.",
      results: [
        "Zero downtime during migration",
        "100% data integrity preserved across 18 months of recordings",
        "Passed SEC and FINRA audits on first attempt post-migration",
        "40% reduction in storage costs through intelligent tiering",
      ],
      testimonial: {
        quote: "We couldn't afford even five minutes of recording loss. RedeemSoft's team understood the stakes and executed flawlessly.",
        author: "VP of Infrastructure Technology, Top 10 Global Investment Bank",
        company: "",
      },
    },
    {
      industry: "Enterprise SaaS",
      title: "Rapid Engineering Team Scale-Up for Product Launch",
      challenge: "A fast-growing SaaS company needed 18 senior engineers across Java, React, and DevOps within six weeks to meet an enterprise product launch deadline. Their internal recruiting team had been struggling for two months.",
      solution: "We deployed a dedicated RPO team that mapped the market, activated passive candidate networks, and ran parallel interview tracks for each role. All 18 hires were onboarded before the deadline.",
      results: [
        "18 of 18 positions filled in 5.5 weeks",
        "94% offer acceptance rate",
        "Zero early attrition in first 12 months",
        "Product launched on schedule with full engineering capacity",
      ],
      testimonial: {
        quote: "I was skeptical anyone could fill 18 roles that fast with quality candidates. RedeemSoft proved me wrong.",
        author: "CTO, Enterprise SaaS Platform (Nasdaq-listed)",
        company: "",
      },
    },
    {
      industry: "Regional Banking",
      title: "Voice Recording Infrastructure Overhaul for 600 Branches",
      challenge: "A regional bank with 600 branches needed to upgrade their voice recording systems to meet new regulatory requirements while maintaining operations across all locations during the transition.",
      solution: "We architected a centralized recording platform with edge capture at branch locations, implemented automated compliance monitoring, and trained the bank's operations team on the new supervisor dashboard.",
      results: [
        "600 branches upgraded in 12 weeks",
        "Zero customer-impacting incidents during rollout",
        "35% reduction in compliance reporting preparation time",
        "Onboarded 3 dedicated support engineers through our staffing practice",
      ],
      testimonial: {
        quote: "They handled the technology implementation and then found us the people to run it. That combination is rare and incredibly valuable.",
        author: "Chief Information Officer, Top 50 U.S. Bank",
        company: "",
      },
    },
  ],
};

export const certificationsData = {
  label: "Security & Compliance",
  headline: "The security posture financial institutions require before they'll sign a contract.",
  subheadline:
    "Every technology deployment includes security architecture review. Every engagement begins with an NDA if needed. Every professional we place undergoes background verification. These are prerequisites, not optional add-ons.",
  certifications: [
    {
      name: "Annual Independent Security Assessment",
      description: "Our infrastructure and delivery operations undergo annual third-party security assessments including penetration testing, vulnerability scanning, and configuration reviews. Summary reports are available to clients under NDA.",
      icon: "ri-shield-check-line",
    },
    {
      name: "Background-Verified Delivery Teams",
      description: "Every technology professional and consultant we deploy undergoes identity verification, criminal background check, employment verification, and — where client requirements mandate — credit history and security clearance validation through accredited agencies.",
      icon: "ri-user-shared-line",
    },
    {
      name: "GDPR & Data Privacy Compliance Program",
      description: "A documented data privacy framework with data mapping, retention policies, and subject access request procedures. Standard Data Processing Agreements (DPA) are available for client legal review at engagement initiation — no delays, no friction.",
      icon: "ri-file-shield-line",
    },
    {
      name: "Regulatory Framework Expertise",
      description: "Operational knowledge of MiFID II, Dodd-Frank, SEC Rules 17a-3/17a-4, FINRA regulatory notices, HIPAA, and PCI DSS — not from whitepapers, from deploying systems that satisfy their specific audit requirements across hundreds of engagements.",
      icon: "ri-scales-3-line",
    },
    {
      name: "Secure Development Lifecycle (SDLC)",
      description: "OWASP-aligned development practices including threat modeling, static and dynamic analysis, dependency scanning, and peer code review integrated into every technology project delivery — not retrofitted before go-live.",
      icon: "ri-code-s-slash-line",
    },
    {
      name: "Business Continuity & Disaster Recovery",
      description: "Documented and annually tested BCP/DR plans with defined RTO and RPO commitments. Our technology deployments include failover architecture design, backup verification procedures, and client-specific contingency runbooks.",
      icon: "ri-cloud-line",
    },
  ],
};

export const partnersData = {
  label: "Technology Partners",
  headline: "We work with the platforms our clients rely on.",
  subheadline:
    "Our engineers and consultants maintain certifications and deep expertise across the enterprise technology ecosystem. These aren't just logos — they're platforms we deploy and support every day.",
  partners: [
    { name: "Amazon Web Services", icon: "ri-cloud-fill", tier: "Advanced" },
    { name: "Microsoft Azure", icon: "ri-microsoft-fill", tier: "Gold" },
    { name: "Google Cloud", icon: "ri-google-fill", tier: "Partner" },
    { name: "Salesforce", icon: "ri-cloud-fill", tier: "Consulting Partner" },
    { name: "ServiceNow", icon: "ri-settings-3-fill", tier: "Implementation Partner" },
    { name: "Verint", icon: "ri-mic-2-fill", tier: "Reseller & Integrator" },
    { name: "NICE Systems", icon: "ri-voiceprint-fill", tier: "Reseller & Integrator" },
    { name: "Red Box", icon: "ri-record-circle-fill", tier: "Integration Partner" },
  ],
};

export const bankingSolutionsData = {
  label: "Banking Technology",
  headline: "Purpose-built recording and compliance systems for financial institutions.",
  subheadline:
    "When regulators call, your recording systems need to prove compliance — not create more questions. We architect, deploy, and support recording infrastructure that financial institutions have relied on for over 20 years.",
  solutions: [
    {
      icon: "ri-mic-2-line",
      title: "Call Recording Infrastructure",
      description:
        "Unified capture across turret, VoIP, mobile, and softphone. Supports thousands of concurrent channels with sub-second search across years of recordings. Deployed in trading floors, advisory desks, and 24/7 contact centers.",
      scale: "2,000+ concurrent channels",
    },
    {
      icon: "ri-shield-check-line",
      title: "Regulatory Compliance Recording",
      description:
        "MiFID II, SEC Rule 17a-4, FINRA, Dodd-Frank, and GDPR-compliant recording with immutable audit trails, automated retention policies, and supervisor oversight dashboards. Designed to satisfy the strictest regulatory examinations.",
      scale: "18+ months of audit-ready recordings",
    },
    {
      icon: "ri-dashboard-3-line",
      title: "Quality Assurance & Supervision",
      description:
        "Supervisor consoles with random sampling, automated call scoring, anomaly detection, and trend analytics. Transform compliance from a checkbox exercise into an operational intelligence asset.",
      scale: "Automated sampling & scoring",
    },
    {
      icon: "ri-database-2-line",
      title: "Recording Storage & Data Management",
      description:
        "Intelligent tiered storage that reduces costs while maintaining instant access. Automated retention enforcement, encrypted at-rest and in-transit, with configurable redundancy across on-premise and cloud targets.",
      scale: "40% average storage cost reduction",
    },
  ],
};

export const technologiesData = {
  label: "Technical Expertise",
  headline: "The platforms, languages, and frameworks we deploy and staff every day.",
  subheadline:
    "We don't just match keywords on a resume. Our technical screening team includes practicing engineers who evaluate candidates on real-world problem solving — not memorized interview questions.",
  categories: [
    {
      name: "Cloud & Infrastructure",
      techs: ["AWS", "Microsoft Azure", "Google Cloud", "Kubernetes", "Terraform", "Docker"],
      proficiency: 95,
    },
    {
      name: "Enterprise Development",
      techs: ["Java / Spring", "Python", "C# / .NET", "Node.js", "Go", "Rust"],
      proficiency: 92,
    },
    {
      name: "Frontend & Mobile",
      techs: ["React", "Angular", "Vue.js", "React Native", "Flutter", "Swift / Kotlin"],
      proficiency: 88,
    },
    {
      name: "Data & Analytics",
      techs: ["Snowflake", "Databricks", "SQL Server", "PostgreSQL", "MongoDB", "Redis"],
      proficiency: 90,
    },
    {
      name: "DevOps & Security",
      techs: ["Jenkins", "GitHub Actions", "Splunk", "CrowdStrike", "Okta", "Vault"],
      proficiency: 93,
    },
    {
      name: "Voice & Recording Platforms",
      techs: ["Verint", "NICE NTR", "Red Box", "ASC", "IPC", "Speakerbus"],
      proficiency: 96,
    },
  ],
};

export const testimonialsData = {
  label: "Client Trust",
  headline: "What enterprise leaders say about working with us.",
  subheadline:
    "We could write a paragraph about our commitment to quality. Our clients say it better.",
  testimonials: [
    {
      quote:
        "We evaluated six recording vendors. RedeemSoft was the only one who could explain — in detail — how their architecture would satisfy our specific SEC and FINRA requirements. They didn't just sell us a product. They solved our compliance problem.",
      author: "SVP, Technology Infrastructure",
      company: "Top 25 U.S. Regional Bank",
      industry: "Banking",
      initials: "MT",
    },
    {
      quote:
        "Finding 12 senior Java developers who actually understand distributed systems — in three weeks — seemed impossible. RedeemSoft delivered 14 candidates, 12 of whom we hired. Eighteen months later, every single one is still with us.",
      author: "VP of Engineering",
      company: "Enterprise SaaS Platform (Nasdaq-listed)",
      industry: "Enterprise Technology",
      initials: "SC",
    },
    {
      quote:
        "The recording migration we thought would take six months was completed in fourteen weeks. More importantly, our first regulatory audit post-migration was the smoothest we've ever had. The examiners specifically noted the clarity of our recording audit trails.",
      author: "Director of Infrastructure",
      company: "Global Investment Bank",
      industry: "Capital Markets",
      initials: "DP",
    },
    {
      quote:
        "Switching to RedeemSoft's RPO model reduced our engineering cost-per-hire by 34% in the first year. But what really mattered was the quality improvement — our hiring manager satisfaction scores went from 3.2 to 4.7 out of 5.",
      author: "Chief People Officer",
      company: "Healthcare Technology Firm",
      industry: "Health Tech",
      initials: "JW",
    },
    {
      quote:
        "We had a critical CTO search that had been open for four months with another firm. RedeemSoft presented three exceptional candidates within five weeks, and we hired one of them. She's been transformational for our engineering culture.",
      author: "CEO",
      company: "Fintech Platform (Series D)",
      industry: "Financial Technology",
      initials: "RK",
    },
  ],
};

export const awardsData = {
  label: "Recognition",
  headline: "Recognition from the communities and industries we serve.",
  subheadline: "",
  awards: [
    { name: "Top IT Staffing Firm — Northeast", org: "Staffing Industry Analysts", year: "2025" },
    { name: "Best Compliance Technology Partner", org: "Banking Tech Awards", year: "2024" },
    { name: "Fastest-Growing Private Company", org: "Inc. 5000", year: "2023" },
    { name: "Excellence in Enterprise Service", org: "NJ Tech Council", year: "2025" },
    { name: "Top Workplace — Small Business", org: "NJ.com", year: "2024" },
    { name: "Diversity in Technology Leadership", org: "Tech Inclusion Forum", year: "2023" },
  ],
};

export const metricsData = {
  label: "By The Numbers",
  headline: "The numbers behind our reputation.",
  subheadline: "",
  metrics: [
    { value: "98%", label: "Client Retention Rate", detail: "Over the last decade" },
    { value: "14 Days", label: "Average Contract Time-to-Fill", detail: "For IT staffing engagements" },
    { value: "50+", label: "Enterprise Organizations", detail: "Served across 6 regulated industries" },
    { value: "0", label: "Failed Compliance Audits", detail: "After our recording system deployments" },
    { value: "2,400+", label: "Recording Channels Deployed", detail: "Across trading floors globally" },
    { value: "34%", label: "Average Cost-per-Hire Reduction", detail: "Through our RPO programs" },
  ],
};

export const faqData = {
  headline: "Questions enterprise clients ask us.",
  subheadline:
    "Honest answers from two decades of working inside banks, trading floors, and enterprise IT organizations.",
  faqs: [
    {
      question: "What makes your compliance recording different from off-the-shelf solutions?",
      answer:
        "Off-the-shelf recording products give you capture and storage. We deliver a complete compliance architecture: capture across all communication channels, tamper-evident storage with automated retention enforcement, supervisor oversight dashboards with anomaly detection, and a full audit trail that regulators can review independently. More importantly, we configure the system to your specific regulatory obligations — not the other way around. When an SEC or FINRA examiner asks for a specific recording from 18 months ago, our clients can produce it in under 60 seconds.",
    },
    {
      question: "How do you maintain quality at the speed you promise for IT staffing?",
      answer:
        "We don't take shortcuts. Our speed comes from preparation: a curated, pre-vetted network of technology professionals we've already assessed technically and behaviorally. When you give us a role, we're not starting from scratch — we're activating an existing relationship. Every candidate still goes through skills verification, structured behavioral interview, and background check specific to your requirements. Our 94% offer acceptance rate and near-zero early attrition suggest the process works.",
    },
    {
      question: "Can you handle both technology implementation and staffing for the same engagement?",
      answer:
        "That's exactly the model we built the company around. A common scenario: we deploy a compliance recording platform across your trading floors and — recognizing you'll need engineers to manage it — we simultaneously run a search for the operations team you need. You get one accountable partner, one point of escalation, and a team that already understands your systems from day one. Several of our longest client relationships started this way.",
    },
    {
      question: "What regulatory frameworks do your recording systems support?",
      answer:
        "MiFID II, Dodd-Frank, SEC Rules 17a-3 and 17a-4, FINRA regulatory notices, GDPR, and PCI DSS. We maintain an internal regulatory monitoring function that tracks changes to these frameworks and proactively updates our client deployments. If a new SEC interpretive release changes recording retention requirements, we'll flag it for you before your compliance team reads about it.",
    },
    {
      question: "Where do you deliver services geographically?",
      answer:
        "Headquartered in Iselin, New Jersey, we deliver across the United States directly. Through our vetted partner network, we have delivery capability in the United Kingdom, Germany, Singapore, and India. For technology projects, we can deploy teams on-site, remote, or hybrid depending on your requirements and regulatory constraints.",
    },
    {
      question: "How do you handle sensitive information during executive searches?",
      answer:
        "Executive searches are managed by a separate, ring-fenced team with additional confidentiality protocols. Candidate identities, compensation details, and search progress are shared on a strict need-to-know basis through encrypted channels. We've managed CEO, CTO, and board-level searches where discretion was non-negotiable — and we've never had a breach.",
    },
    {
      question: "What does your procurement and vendor onboarding process look like?",
      answer:
        "We're experienced with enterprise procurement workflows. We can provide our standard MSA and DPA for your legal review, or work within your organization's paper when preferred. We're registered in Dun & Bradstreet, support Ariba, Coupa, and SAP Fieldglass, and can complete most vendor registration packets within five business days. Insurance certificates, security documentation, and financial references are available at engagement initiation.",
    },
    {
      question: "Do you hold professional liability and cyber insurance?",
      answer:
        "Yes. We maintain Professional Liability (Errors & Omissions), Cyber Liability including data breach response coverage, and General Liability insurance — all with an A-rated carrier. Certificates of insurance are available on request and we're accustomed to naming clients as additional insureds per MSA requirements.",
    },
    {
      question: "Can we conduct a security assessment or third-party risk review of your operations?",
      answer:
        "Absolutely. We welcome client security assessments and third-party risk reviews. Our standard MSA includes client audit provisions. We maintain organized security documentation including penetration test summaries, access control policies, business continuity test results, and control evidence mapped to common frameworks. We can also complete your security questionnaire — our average turnaround on standard SIG Lite questionnaires is under five business days.",
    },
    {
      question: "Are you a certified diverse supplier?",
      answer:
        "Yes, RedeemSoft is a certified minority-owned business enterprise. We support our clients' supplier diversity programs and can provide our diversity certification documentation as part of the vendor registration process. We also maintain our own internal commitment to diverse hiring and inclusive workplace practices.",
    },
  ],
};

export const contactData = {
  label: "Start a Conversation",
  headline: "Tell us what you're trying to solve.",
  subheadline:
    "Whether it's a compliance recording deployment, a critical hire, or a conversation about whether we're the right fit — our senior team reads every inquiry and responds within one business day.",
  info: {
    phone: "+1 (732) 548-8002",
    email: "info@redeemsoft.com",
    address: "100 Wood Avenue South, Suite 107, Iselin, NJ 08830",
    hours: "Monday – Friday, 9:00 AM – 6:00 PM EST",
  },
  socialProof: [
    { metric: "98%", label: "Client Retention" },
    { metric: "<24h", label: "Response Time" },
    { metric: "50+", label: "Enterprise Clients" },
  ],
};

export const jobSeekersData = {
  label: "For Job Seekers",
  headline: "Your next career move starts with the right partner.",
  subheadline:
    "RedeemSoft connects technology professionals with opportunities at banks, fintech firms, and enterprise organizations that value quality. Whether you're a senior engineer, a compliance specialist, or a technology leader — we help you find roles where you'll actually matter.",
  benefits: [
    {
      icon: "ri-briefcase-line",
      title: "Verified Enterprise Roles",
      description:
        "We only work with organizations that invest in their people. Every role we represent has been vetted for compensation, culture, and career growth potential.",
    },
    {
      icon: "ri-timer-line",
      title: "Fast, Respectful Process",
      description:
        "Average time from first conversation to offer: under three weeks. We don't ghost candidates. You'll know where you stand at every step.",
    },
    {
      icon: "ri-user-search-line",
      title: "Advocacy, Not Automation",
      description:
        "A real recruiter will learn your skills, your goals, and what you actually want — then advocate for you with hiring managers who have the budget and authority to move fast.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Confidential Searches",
      description:
        "Exploring options while employed? We protect your identity until you're ready to engage. No resumes sent without your explicit approval.",
    },
  ],
  formNote:
    "Submit your details below and a RedeemSoft recruiter will reach out within 48 hours. No spam, no mass emails — just real conversations about real roles.",
};

export const globalPresenceData = {
  headquarters: {
    city: "Iselin, New Jersey",
    address: "100 Wood Avenue South, Suite 107, Iselin, NJ 08830",
    phone: "+1 (732) 548-8002",
  },
  offices: [
    { city: "New York, NY", role: "Sales & Delivery" },
    { city: "London, UK", role: "European Delivery" },
    { city: "Hyderabad, India", role: "Offshore Development Center" },
    { city: "Singapore", role: "APAC Operations" },
    { city: "Toronto, Canada", role: "North American Expansion" },
  ],
  note: "Headquartered in New Jersey with delivery capability across North America, Europe, and Asia through our partner network.",
};
export const footerData = {
  company: "RedeemSoft, LLC",
  tagline:
    "Enterprise technology solutions and IT recruitment services trusted by banks and businesses since 2004. One partner for secure systems and quality talent.",
  newsletter: {
    placeholder: "Your work email",
    button: "Subscribe",
    disclaimer: "No spam. Industry insights and occasional updates only.",
  },
  links: [
    {
      title: "Services",
      items: [
        { label: "Banking Technology", href: "#services" },
        { label: "IT Staffing", href: "#services" },
        { label: "Compliance Recording", href: "#banking" },
        { label: "Digital Transformation", href: "#services" },
      ],
    },
    {
      title: "Company",
      items: [
        { label: "About Us", href: "#about" },
        { label: "Case Studies", href: "#case-studies" },
        { label: "Industries", href: "#industries" },
        { label: "Contact", href: "#contact" },
      ],
    },
    {
      title: "Resources",
      items: [
        { label: "FAQ", href: "#faq" },
        { label: "Client Stories", href: "#testimonials" },
        { label: "Technology Partners", href: "#partners" },
        { label: "Security & Compliance", href: "#certifications" },
      ],
    },
  ],
  bottom: "© 2026 RedeemSoft, LLC. All rights reserved.",
};

export const enterpriseTrustData = {
  label: "Enterprise Trust",
  headline: "What procurement teams need to see before they'll shortlist a vendor.",
  subheadline:
    "When UBS, Morgan Stanley, or BNP Paribas evaluates a technology partner, they're not just buying services — they're assessing operational risk. Here's what we have ready before the first conversation.",
  sections: [
    {
      icon: "ri-shield-check-line",
      title: "Insurance & Liability",
      items: [
        { label: "Professional Liability (E&O)", detail: "Coverage maintained with A-rated carrier; certificate available on request" },
        { label: "Cyber Liability", detail: "Comprehensive cyber insurance including data breach response and third-party liability" },
        { label: "General Liability & Workers' Comp", detail: "Standard commercial coverage as required by client MSA terms" },
      ],
    },
    {
      icon: "ri-file-text-line",
      title: "Contractual Readiness",
      items: [
        { label: "Master Services Agreement (MSA)", detail: "Standard MSA available for client legal review; we also work within client paper when preferred" },
        { label: "Data Processing Agreement (DPA)", detail: "GDPR-compliant DPA with standard contractual clauses; provided at engagement initiation" },
        { label: "Service Level Agreements (SLA)", detail: "Defined response and resolution time commitments with escalation matrix and quarterly performance reviews" },
      ],
    },
    {
      icon: "ri-building-2-line",
      title: "Supplier Profile",
      items: [
        { label: "D-U-N-S Registered", detail: "Registered and verified in Dun & Bradstreet; DUNS number available for your vendor registration system" },
        { label: "Supplier Diversity", detail: "Certified minority-owned business enterprise; we support our clients' supplier diversity program requirements" },
        { label: "Procurement Systems", detail: "Experienced with Ariba, Coupa, SAP Fieldglass, and client-specific vendor onboarding portals" },
      ],
    },
    {
      icon: "ri-lock-line",
      title: "Security Operations",
      items: [
        { label: "Annual Penetration Testing", detail: "External and internal network penetration tests conducted annually by independent third-party firm" },
        { label: "Vulnerability Disclosure", detail: "Security contact: security@redeemsoft.com — acknowledged within 24 hours, triaged within 72 hours" },
        { label: "Access Control & Auditing", detail: "Role-based access, multi-factor authentication, detailed audit logging on all production systems" },
      ],
    },
    {
      icon: "ri-database-2-line",
      title: "Business Continuity",
      items: [
        { label: "Tested DR Plans", detail: "Business continuity and disaster recovery plans tested annually with documented results available under NDA" },
        { label: "RTO / RPO Commitments", detail: "Defined Recovery Time and Recovery Point Objectives per service tier; committed in writing" },
        { label: "Incident Response", detail: "Documented incident response procedure with defined roles, communication protocols, and post-incident review process" },
      ],
    },
    {
      icon: "ri-scales-3-line",
      title: "Regulatory Readiness",
      items: [
        { label: "Client Audit Rights", detail: "Standard MSA includes client audit provisions; we welcome your third-party risk assessment team" },
        { label: "Compliance Documentation", detail: "Security policy documentation, risk assessment reports, and control evidence organized for vendor due diligence" },
        { label: "Regulatory Change Monitoring", detail: "Internal function tracking changes to SEC, FINRA, GDPR, and other relevant regulatory frameworks" },
      ],
    },
  ],
};